import React, { useState, useRef, useEffect } from 'react';
import { Facebook, Instagram, Youtube, Twitter, MessageCircle, X, Bot, User, Clock } from 'lucide-react';



  const ChatInterface = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [showCount, setShowCount] = useState(true);
    const [lastBotActivity, setLastBotActivity] = useState(new Date());
    const scrollRef = useRef(null);
    
    const suggestions = [
      "What is the RUT241 router?",
      "Do you have 5G routers?",
      "What's the difference between RUT956 and RUT241?",
      "Can I use these routers internationally?",
      "What are the temperature ranges for your routers?",
      "Do you offer eSIM routers?",
      "How many Ethernet ports do your routers have?",
      "What VPN protocols do you support?"
    ];
  
    const botResponses = {
      product: [
        {
          keywords: ['rut241',  'what is'],
          responses: [
            "The RUT241 is a professional cellular router designed for industrial applications. It features 4G LTE Cat 4, dual Ethernet ports, and Wi-Fi connectivity.",
            "RUT241 is an industrial-grade router with 4G LTE capabilities, Wi-Fi, and digital I/O functionality. It's ideal for remote monitoring and connectivity solutions."
          ]
        },
        {
          keywords: ['5g', '5g router', 'rutm50'],
          responses: [
            "Yes! The RUTm50 is our 5G industrial router. It supports 5G Sub-6 GHz SA/NSA with up to 3.4Gbps download speeds and 4x4 MIMO technology.",
            "Yes! Our RUTm50 offers cutting-edge 5G connectivity with Sub-6 GHz support, making it perfect for high-bandwidth industrial applications."
          ]
        },
        {
          keywords: ['difference', 'compare', 'rut956', 'rut241'],
          responses: [
            "The RUT956 and RUT241 are both industrial routers, but the RUT956 focuses more on enhanced security features, while the RUT241 offers versatile connectivity with digital I/O.",
            "Key differences include: RUT956 has IP55 rating and PoE capabilities, while RUT241 has dual Ethernet ports and Wi-Fi connectivity. Both offer robust 4G LTE support."
          ]
        },
        {
          keywords: ['international', 'global', 'worldwide'],
          responses: [
            "Our routers are designed for global use. They support multiple cellular bands (2G, 3G, 4G) and can be used in many countries with the right SIM card.",
            "Yes, our routers like RUTm50 and RUT241 support international use with multi-band cellular modules and flexible SIM configurations."
          ]
        },
        {
          keywords: ['temperature', 'cold', 'hot', 'range'],
          responses: [
            "All our industrial routers, including RUT241 and RUT956, are designed to operate in extreme temperatures from -40°C to 75°C, making them suitable for harsh environments.",
            "Our routers feature an impressive operating temperature range of -40°C to 75°C, ensuring reliable performance in both very cold and hot conditions."
          ]
        },
        {
          keywords: ['esim', 'rut241 esim'],
          responses: [
            "Yes, we offer the RUT241 eSIM variant, which provides the same robust features as the standard RUT241 but with integrated eSIM technology for easier connectivity.",
            "The RUT241 eSIM router provides flexibility in connectivity, allowing easier network switching and remote SIM provisioning without physical SIM card swapping."
          ]
        },
        {
          keywords: ['ethernet', 'ports', 'network'],
          responses: [
            "Our router Ethernet port configuration varies: RUT241 has 1 WAN and 1 LAN port, while the RUTm50 offers 1 WAN and 4 LAN ports at 10/100/1000 Mbps.",
            "Depending on the model, our routers provide 1-4 Ethernet ports. The RUTm50 stands out with its 4 LAN ports, ideal for complex network setups."
          ]
        },
        {
          keywords: ['vpn', 'protocol', 'security'],
          responses: [
            "Our RUTm50 supports multiple VPN protocols including OpenVPN, IPsec, WireGuard, Tailscale, and DMVPN, ensuring flexible and secure remote connectivity.",
            "We offer comprehensive VPN support across our router lineup, with protocols like OpenVPN, IPsec, and WireGuard to meet various security and connectivity needs."
          ]
        }
      ],
      default: [
        "Thank you for your interest in Ejil Enterprise! How can I help you today?",
        "Thank you for your message! Our team will get back to you shortly.",
        "We're experts in industrial cellular networking. What specific information are you looking for?",
        "Ejil Enterprise offers robust industrial routers. Feel free to ask about our product lineup!"
      ]
    };
  
  
    const getBotResponse = (userMessage) => {
      const lowercaseMessage = userMessage.toLowerCase();
      
      for (let category of botResponses.product) {
        if (category.keywords.some(keyword => lowercaseMessage.includes(keyword))) {
          return category.responses[Math.floor(Math.random() * category.responses.length)];
        }
      }
  
      return botResponses.default[Math.floor(Math.random() * botResponses.default.length)];
    };
  
  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      
      // Set initial message only for desktop
      if (!isMobileDevice && messages.length === 0) {
        setMessages([{
          text: "How can I help you today? 👋",
          type: 'bot',
          timestamp: new Date()
        }]);
      }
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const formatLastActive = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      const userMessage = inputText;
      setMessages(prev => [...prev, { text: userMessage, type: 'user', timestamp: new Date() }]);
      setInputText('');
      
      setTimeout(() => {
        const botResponseTime = new Date();
        const botResponse = getBotResponse(userMessage);
        
        setLastBotActivity(botResponseTime);
        setMessages(prev => [...prev, {
          text: botResponse,
          type: 'bot',
          timestamp: botResponseTime
        }]);
      }, 1000);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputText(suggestion);
  };

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setShowCount(false);
    }
  };
  const getChatContainerStyles = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        margin: 0,
        borderRadius: 0,
        zIndex: 1000,
      };
    }
    return {
      position: 'fixed',
      bottom: '100px',
      right: '30px',
      width: '380px',
      height: '450px',
      borderRadius: '12px',
      zIndex: 1000,
    };
  };
  const getBotMessageCount = () => {
    return messages.filter(m => m.type === 'bot').length;
  };

  return (
    <div style={{
      minHeight: '10vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {/* Floating Chat Button with Message Count and Hover Preview */}
      {(!isChatOpen || !isMobile) && (
        <div 
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            zIndex: 1001,
          }}
          onMouseEnter={() => !isMobile && setIsHovering(true)}
          onMouseLeave={() => !isMobile && setIsHovering(false)}
        >
          {/* Message Preview on Hover - only show on desktop */}
          {!isMobile && isHovering && !isChatOpen && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '0',
              backgroundColor: 'white',
              padding: '12px 20px',
              borderRadius: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              border: '1px solid #400684',
              color: '#400684',
              fontWeight: '500',
              maxWidth: '250px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              How can I help you today? 👋
            </div>
          )}

          {/* Chat Button */}
          <div style={{ position: 'relative' }}>
            <button 
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#DC2666',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              onClick={handleChatToggle}
            >
              {isChatOpen && !isMobile ? <X color="white" size={24} /> : <MessageCircle color="white" size={24} />}
            </button>
            
            {/* Message Count Badge - Now showing on both mobile and desktop */}
            {!isChatOpen && showCount && getBotMessageCount() > 0 && (
              <div style={{
                position: 'absolute',
                top: -5,
                right: -5,
                backgroundColor: '#400684',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                border: '2px solid white',
              }}>
                {getBotMessageCount()}
              </div>
            )}
          </div>
        </div>
      )}

    
{isChatOpen && (
  <div style={{
    ...getChatContainerStyles(),
    backgroundColor: '#fff',
    boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }}>
    {/* Chat Header */}
    <div style={{
      padding: '20px',
      backgroundColor: '#400684',
      borderTopLeftRadius: isMobile ? '0' : '12px',
      borderTopRightRadius: isMobile ? '0' : '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h3 style={{
          color: '#fff',
          margin: 0,
          fontSize: '18px',
          fontWeight: '500',
        }}>Chat Support</h3>
        <button 
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          onClick={() => setIsChatOpen(false)}
        >
          <X size={20} />
        </button>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '12px',
      }}>
        <Clock size={14} />
        <span>Last active {formatLastActive(lastBotActivity)}</span>
      </div>
    </div>

    {/* Messages Area */}
    <div 
            ref={scrollRef}
            style={{
              flexGrow: 1,
              overflow: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              backgroundColor: '#f8f9fa',
              scrollbarWidth: 'thin', // For Firefox
              scrollbarColor: '#888 #f1f1f1', // For Firefox
            }}
         
            className="custom-scrollbar"
          >
      {messages.map((message, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '10px',
          flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: message.type === 'user' ? '#400684' : '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            {message.type === 'user' ? 
              <User size={20} color="#ffffff" /> : 
              <Bot size={20} color="#400684" />
            }
          </div>
          <div style={{
            backgroundColor: message.type === 'user' ? '#400684' : '#ffffff',
            color: message.type === 'user' ? '#ffffff' : '#333333',
            padding: '12px 16px',
            borderRadius: '12px',
            maxWidth: '70%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            wordWrap: 'break-word',
            fontSize: '14px',
            lineHeight: '1.5',
          }}>
            <div>{message.text}</div>
            <div style={{
              fontSize: '10px',
              marginTop: '4px',
              opacity: 0.7,
              textAlign: message.type === 'user' ? 'right' : 'left',
            }}>
              {formatLastActive(message.timestamp)}
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Input Area */}
    <div style={{
      padding: '15px',
      borderTop: '1px solid #eee',
      backgroundColor: '#ffffff',
      position: 'relative',
    }}>
      {/* Suggestions Area */}
      <div style={{
        marginBottom: '15px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        <div style={{
          display: 'inline-flex',
          gap: '8px',
          padding: '4px',
        }}>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#400684',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.2s',
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

      {/* Input Form */}
      <form 
        onSubmit={handleSubmit} 
        style={{
          display: 'flex',
          gap: '10px',
          paddingBottom: isMobile ? '20px' : '0',
        }}
      >
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          style={{
            flexGrow: 1,
            padding: '12px',
            border: '1px solid #e1e1e1',
            borderRadius: '8px',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: '#f8f9fa',
          }}
          placeholder="Type your message..."
        />
        <button 
          type="submit" 
          style={{
            padding: '12px 20px',
            backgroundColor: '#400684',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'background-color 0.2s',
            whiteSpace: 'nowrap',
          }}
        >
          Send
        </button>
      </form>
    </div>
  </div>
)}

<style jsx>{`
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px; // Very narrow scrollbar
            }
            
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #400684; // Light track color
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #400684; // Dark scrollbar thumb
              border-radius: 3px;
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #400684; // Darker on hover
            }
          `}</style>


      {/* Footer */}
      <footer style={{
        backgroundColor: '#400684',
        padding: '15px 20px',
        marginTop: 'auto',
      }}>
        <div style={{
          textAlign: 'center',
          color: '#ffffff',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px',
            flexWrap: 'wrap',
          }}>
            <div style={{
              width: isMobile ? '30px' : '50px',
              height: '2px',
              backgroundColor: '#ffffff',
              margin: '10px',
            }}></div>
            <div style={{
              display: 'flex',
              gap: '15px',
            }}>
              <Facebook style={{ color: '#ffffff', cursor: 'pointer', width: '24px', height: '24px' }} />
              <Instagram style={{ color: '#ffffff', cursor: 'pointer', width: '24px', height: '24px' }} />
              <Youtube style={{ color: '#ffffff', cursor: 'pointer', width: '24px', height: '24px' }} />
              <Twitter style={{ color: '#ffffff', cursor: 'pointer', width: '24px', height: '24px' }} />
            </div>
            <div style={{
              width: isMobile ? '30px' : '50px',
              height: '2px',
              backgroundColor: '#ffffff',
              margin: '10px',
            }}></div>
          </div>
          <div style={{
            marginBottom: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <img 
              src="/logo.png" 
              alt="Logo" 
              style={{
                width: isMobile ? '60px' : '80px',
                height: 'auto',
                marginBottom: '5px',
              }}
            />
            <p style={{
              fontSize: '14px',
              color: '#a8a8a8',
              margin: '5px 0',
              textAlign: 'center',
            }}>
              Copyright © 2024 Ejil Enterprise, Inc.
            </p>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '8px',
            fontSize: isMobile ? '12px' : '14px',
          }}>
            <a style={{ color: '#a8a8a8', textDecoration: 'none', cursor: 'pointer' }} href="#">Legal Stuff</a>
            <span style={{ color: '#a8a8a8' }}>|</span>
            <a style={{ color: '#a8a8a8', textDecoration: 'none', cursor: 'pointer' }} href="#">Privacy Policy</a>
            <span style={{ color: '#a8a8a8' }}>|</span>
            <a style={{ color: '#a8a8a8', textDecoration: 'none', cursor: 'pointer' }} href="#">Security</a>
            <span style={{ color: '#a8a8a8' }}>|</span>
            <a style={{ color: '#a8a8a8', textDecoration: 'none', cursor: 'pointer' }} href="#">Website Accessibility</a>
            <span style={{ color: '#a8a8a8' }}>|</span>
            <a style={{ color: '#a8a8a8', textDecoration: 'none', cursor: 'pointer' }} href="#">Manage Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChatInterface;